import { CloseRounded, SendRounded } from '@mui/icons-material';
import {
    Box,
    IconButton,
    InputBase,
    List,
    ListItem,
    ListItemText,
    Modal,
    Paper,
    Typography,
} from '@mui/material';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/useAuth';
import useSettings from '../../hooks/useSettings';
import ListEmpty from '../fragments/ListEmpty';
import ListLoading from '../fragments/ListLoading';

export default function ChatSheet({ open, onClose, jitsiApi }) {
    const [text, setText] = useState('');
    const [messages, setMessages] = useState([]);

    const location = useLocation();

    const { themeMode } = useSettings();
    const { enqueueSnackbar } = useSnackbar();
    const { user } = useAuth();

    const onSend = () => {
        jitsiApi?.executeCommand('sendChatMessage', text, '', true);
        setText('');
    };

    // remove duplicates from message array
    // messages

    const messageWithoutDuplicates = messages?.reduce((arr, current) => {
        const dup = arr.find((item) => item?.message === current?.message);
        if (!dup) {
            return arr.concat([current]);
        } else {
            return arr;
        }
    }, []);

    const addMessageToChat = useCallback(
        (chat, you) => {
            // check if message is already in chat
            if (messages?.find((m) => m?.message == chat?.message)) return;

            const id = Math.random() * 100000;

            setMessages((prev) => [
                ...prev,
                { ...chat, you, date: new Date(), id },
            ]);
        },
        [messages]
    );

    useEffect(() => {
        jitsiApi?.addListener('incomingMessage', function (chat) {
            addMessageToChat(chat, chat?.nick == user?.username);

            !open &&
                enqueueSnackbar(`${chat?.nick}: ${chat?.message}`, {
                    variant: 'info',
                });
        });

        return () => jitsiApi?.removeListener('incomingMessage', () => {});
    }, [addMessageToChat, enqueueSnackbar, jitsiApi, open, user?.username]);

    useEffect(() => {
        jitsiApi?.addListener('outgoingMessage', function (chat) {
            addMessageToChat(chat, true);
        });

        return () => jitsiApi?.removeListener('outgoingMessage', () => {});
    }, [addMessageToChat, jitsiApi]);

    return (
        <Modal open={open} onClose={onClose} title={location.state?.name}>
            <Paper
                className="w-100"
                sx={{
                    position: 'fixed',
                    py: 4,
                    px: 2,
                    bottom: 0,
                    left: 0,
                    height: window.innerHeight * 0.6,
                    overflow: 'auto',
                }}
            >
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        zIndex: 1,
                    }}
                >
                    <CloseRounded />
                </IconButton>

                <List sx={{ pb: 4, mt: 2 }}>
                    {messageWithoutDuplicates?.map(
                        ({ id, nick, message, you, date }) => (
                            <ListItem
                                className="align-items-start"
                                sx={{ p: 0 }}
                                key={id}
                            >
                                {you && <ListItemText />}
                                <ListItemText
                                    sx={{
                                        p: 1,
                                        flex: 'none',
                                        borderRadius: 1,
                                        border:
                                            themeMode == 'light'
                                                ? `2px solid`
                                                : 'none',
                                        borderColor: 'primary.main',
                                        bgcolor: you
                                            ? 'primary.main'
                                            : 'background.paper',
                                        color: you
                                            ? 'common.white'
                                            : 'text.primary',
                                    }}
                                    primary={`${nick || ''}`}
                                    secondary={
                                        <span>
                                            <Typography color="inherit">
                                                {message}
                                            </Typography>
                                            <Typography variant="body2">
                                                {moment(date).format('hh:mm A')}
                                            </Typography>
                                        </span>
                                    }
                                />
                            </ListItem>
                        )
                    )}
                    {!jitsiApi && <ListLoading />}
                    {jitsiApi && messages?.length < 1 && (
                        <ListEmpty title="No messages yet" />
                    )}
                </List>

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        width: window.innerWidth,
                        p: 1,
                    }}
                >
                    <Paper
                        className="d-flex align-items-center"
                        sx={{ py: 0.5, pl: 2, pr: 1 }}
                        elevation={4}
                    >
                        <InputBase
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            fullWidth
                            placeholder="Message"
                        />
                        <IconButton onClick={onSend}>
                            <SendRounded />
                        </IconButton>
                    </Paper>
                </Box>
            </Paper>
        </Modal>
    );
}
